import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";

export const useTideGraphData = (station) => {
  const [tideData, setTideData] = useState([]);
  const [stationName, setStationName] = useState(null);
  const [preparedData, setPreparedData] = useState([]);
  const [extremePoints, setExtremePoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    const fetchStationName = async () => {
      if (!station || !currentUser) return;

      setLoading(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `/api/tides/name/${station}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json = await response.json();
        setStationName(json);
      } catch (error) {
        console.error("Error fetching station name:", error);
        setError("Failed to load tide station information");
      } finally {
        setLoading(false); // Ensure loading is set to false even on error
      }
    };
    fetchStationName();
  }, [station, currentUser]);

  useEffect(() => {
    const fetchTideData = async () => {
      if (!station || !currentUser) return;

      setLoading(true);
      try {
        const token = await currentUser.getIdToken();
        const response = await fetch(
          `/api/tides/${station}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json = await response.json();
        setTideData(json);
      } catch (error) {
        console.error("Error fetching tide data:", error);
        setError("Failed to load tide data");
      } finally {
        setLoading(false); // Ensure loading is set to false even on error
      }
    };
    fetchTideData();
  }, [station, currentUser]);

  useEffect(() => {
    if (tideData.length > 0) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      const endOfRange = new Date(
        startOfToday.getTime() + 2 * 24 * 60 * 60 * 1000
      );

      const filteredData = tideData.filter((item) => {
        const itemDate = new Date(item.t);
        return itemDate >= startOfToday && itemDate <= endOfRange;
      });

      const prepared = filteredData.map((item) => ({
        t: new Date(item.t).getTime(),
        v: parseFloat(item.v),
      }));

      setPreparedData(prepared);
      findExtremePoints(prepared);
    }
  }, [tideData]);

  const findExtremePoints = (data) => {
    if (data.length < 3) {
      setExtremePoints([]);
      return;
    }

    const extremes = [];

    for (let i = 1; i < data.length - 1; i++) {
      const prev = data[i - 1].v;
      const current = data[i].v;
      const next = data[i + 1].v;

      if (
        (current > prev && current > next) || // High tide
        (current < prev && current < next) // Low tide
      ) {
        extremes.push({
          ...data[i],
          type: current > prev ? "high" : "low",
        });
      }
    }

    // Add the very last point of the dataset as an "end" point for reference if needed
    if (data.length > 0) {
      extremes.push({
        ...data[data.length - 1],
        type: "end",
      });
    }

    setExtremePoints(extremes);
  };

  const getCurrentTideInfo = () => {
    if (preparedData.length < 2)
      return { status: "Unknown", current: null, closestPoint: null };

    const now = new Date().getTime();
    let closestIndex = 0;
    let minTimeDiff = Infinity;

    // Find the data point closest to the current time
    preparedData.forEach((item, index) => {
      const timeDiff = Math.abs(item.t - now);
      if (timeDiff < minTimeDiff) {
        minTimeDiff = timeDiff;
        closestIndex = index;
      }
    });

    const current = preparedData[closestIndex].v;
    const closestPoint = {
      t: new Date(preparedData[closestIndex].t),
      v: preparedData[closestIndex].v,
    };

    let status = "Unknown";

    // Determine if tide is rising or falling
    // If closest point is the very first or last, extrapolate based on the next/previous point
    if (closestIndex === 0 && preparedData.length > 1) {
      status = preparedData[1].v > current ? "Rising" : "Falling";
    } else if (
      closestIndex === preparedData.length - 1 &&
      preparedData.length > 1
    ) {
      status =
        current > preparedData[closestIndex - 1].v ? "Rising" : "Falling";
    } else if (closestIndex > 0 && closestIndex < preparedData.length - 1) {
      // Find the next extreme point to determine overall trend
      let nextExtreme = extremePoints.find(
        (p) => p.t > preparedData[closestIndex].t
      );

      if (nextExtreme) {
        status = nextExtreme.type === "high" ? "Rising" : "Falling";
      } else {
        // Fallback: if no next extreme, determine based on immediate next point
        status =
          preparedData[closestIndex + 1].v > current ? "Rising" : "Falling";
      }
    }

    return {
      status,
      current,
      closestPoint,
    };
  };

  const {
    status: tideStatus,
    current: currentHeight,
    closestPoint,
  } = useMemo(() => getCurrentTideInfo(), [preparedData, extremePoints]);

  const dateRange = useMemo(() => {
    if (preparedData.length === 0) return "";
    const startDate = new Date(preparedData[0].t);
    const endDate = new Date(preparedData[preparedData.length - 1].t);

    const formatDateForDisplay = (date) => {
      if (!date) return "";
      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    };

    const formattedStartDate = formatDateForDisplay(startDate);
    const formattedEndDate = formatDateForDisplay(endDate);

    if (formattedStartDate === formattedEndDate) {
      return formattedStartDate;
    }
    return `${formattedStartDate} - ${formattedEndDate}`;
  }, [preparedData]);

  return {
    preparedData,
    stationName,
    loading,
    error,
    extremePoints,
    tideStatus,
    currentHeight,
    closestPoint,
    dateRange,
  };
};
